import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Field } from '@atlaskit/form';
import type { OptionProps, SingleValueProps, ValueType } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';
import { AnalyticsContext } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages.ts';
import { transformStargateUrl } from '../../utils/configureUtils.ts';
import type { IssueType } from '../../providers/BulkCreateContextProvider.tsx';
import {
	useIsConfigureLoading,
	useIssueTypesActions,
	useIssueTypesState,
} from '../../providers/BulkConfigureContextProvider.tsx';
import { BulkConfigureSelectOption } from '../bulk-work-create-configure/BulkConfigureSelectOption.tsx';

type IssueTypeOptionType = {
	label: string;
	value: IssueType;
	avatarUrl: string;
};

const mapIssueTypeToOption = (issueType: IssueType): IssueTypeOptionType => {
	return {
		label: issueType.name,
		value: issueType,
		avatarUrl: transformStargateUrl(issueType.iconUrl),
	};
};

const IssueTypeOption = (props: OptionProps<IssueTypeOptionType>) => (
	<components.Option {...props}>
		<BulkConfigureSelectOption label={props.label} avatarUrl={props.data.avatarUrl} />
	</components.Option>
);

const IssueTypeSingleValue = (props: SingleValueProps<IssueTypeOptionType>) => (
	<components.SingleValue {...props}>
		<BulkConfigureSelectOption label={props.data.label} avatarUrl={props.data.avatarUrl} />
	</components.SingleValue>
);

// TODO: Remove optional props once we cleanup jira_ai_decouple_issue_create_fields FF
interface IssueTypeSelectorProps {
	optionsIssueTypes?: IssueType[];
	value?: IssueType | null;
	onChange?: (issueType: IssueType | null) => void;
	isLoading?: boolean;
	isDisabled?: boolean;
	isRequired?: boolean;
	label?: string;
}

// TODO: Remove Partial type once we cleanup jira_ai_decouple_issue_create_fields FF
export const IssueTypeSelector: React.FC<Partial<IssueTypeSelectorProps>> = (props) => {
	const { formatMessage } = useIntl();
	const { selectedIssueType, optionsIssueTypes: issueTypes } = useIssueTypesState();
	const { setSelectedIssueType } = useIssueTypesActions();
	const { isLoading: isConfigureLoading } = useIsConfigureLoading();

	// Old Sweet State logic
	const optionsSweetState = useMemo(() => {
		return issueTypes?.map(mapIssueTypeToOption);
	}, [issueTypes]);

	const onChangeSweetState = useCallback(
		(selectedOption: ValueType<IssueTypeOptionType>) => {
			if (selectedOption && selectedOption.value !== selectedIssueType) {
				void setSelectedIssueType(selectedOption.value);
			}
		},
		[selectedIssueType, setSelectedIssueType],
	);

	const selectedValueSweetState = selectedIssueType
		? mapIssueTypeToOption(selectedIssueType)
		: null;

	// New decoupled logic (props/local state)
	const {
		optionsIssueTypes = [],
		value: controlledValue,
		onChange,
		isLoading = false,
		isDisabled = false,
		label,
	} = props;

	const [localValue, setLocalValue] = useState<IssueType | null>(controlledValue ?? null);

	useEffect(() => {
		if (controlledValue !== undefined) setLocalValue(controlledValue);
	}, [controlledValue]);

	const mappedOptions = useMemo(
		() => optionsIssueTypes.map(mapIssueTypeToOption),
		[optionsIssueTypes],
	);

	if (!fg('jira_ai_decouple_issue_create_fields')) {
		// Old Sweet State logic

		return (
			<Field<ValueType<IssueTypeOptionType>>
				name="defaultIssueTypes"
				label={formatMessage(
					fg('confluence-issue-terminology-refresh')
						? messages.bulkConfigureFormDefaultIssueTypeIssueTermRefresh
						: messages.bulkConfigureFormDefaultIssueType,
				)}
				testId="work-create-issue-type-field"
			>
				{({ fieldProps: { id, ...rest } }) => (
					<Fragment>
						<Select<IssueTypeOptionType>
							inputId={id}
							{...rest}
							options={optionsSweetState}
							isDisabled={isConfigureLoading || !selectedValueSweetState}
							isLoading={isConfigureLoading}
							value={selectedValueSweetState}
							onChange={onChangeSweetState}
							components={{
								Option: IssueTypeOption,
								SingleValue: IssueTypeSingleValue,
							}}
						/>
					</Fragment>
				)}
			</Field>
		);
	}

	const handleChange = (selectedOption: ValueType<IssueTypeOptionType>) => {
		if (!selectedOption || !selectedOption.value) {
			return;
		}

		if (selectedOption.value === localValue) {
			return;
		}

		setLocalValue(selectedOption.value);
		onChange?.(selectedOption.value);
	};

	const selectedValue = localValue ? mapIssueTypeToOption(localValue) : null;

	return (
		<Field<ValueType<IssueTypeOptionType>>
			name="defaultIssueTypes"
			// TODO: Remove formatMessage once we cleanup jira_ai_decouple_issue_create_fields FF
			label={
				label ??
				formatMessage(
					fg('confluence-issue-terminology-refresh')
						? messages.bulkConfigureFormDefaultIssueTypeIssueTermRefresh
						: messages.bulkConfigureFormDefaultIssueType,
				)
			}
			testId="work-create-issue-type-field"
			isRequired={props.isRequired ?? false}
		>
			{({ fieldProps: { id, ...rest } }) => (
				<AnalyticsContext data={{ attributes: { fieldName: 'workCreateFormIssueTypeSelector' } }}>
					<Select<IssueTypeOptionType>
						inputId={id}
						{...rest}
						value={selectedValue}
						options={mappedOptions}
						onChange={handleChange}
						isLoading={isLoading}
						isDisabled={isLoading || isDisabled || !selectedValue}
						components={{
							Option: IssueTypeOption,
							SingleValue: IssueTypeSingleValue,
						}}
					/>
				</AnalyticsContext>
			)}
		</Field>
	);
};
